import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";

import Loader from "../../../Loader/Loader";
import { getDeviceList } from "../../telemetry-utils";
import { trimAssetName } from "../../../../Common/EndPoints";
import ShuntSignalCircuitView from "./ShuntSignalCircuitView";
import { getSignalInformation } from "./circuit-view-signal-utils";
import { getDeviceTypeForLabel } from "../point-machine/pm-circuit-view-utils";
import { selectedCustomersIdsSelector } from "../../../../features/authentication/auth-slice";
import { getMainSignalDataMap } from "./main-signal-utils";
import { ReactComponent as SignalSVG1Aspect } from "./signal_1asp.svg";
import { ReactComponent as SignalSVG2Aspect } from "./signal_2asp.svg";
import { ReactComponent as SignalSVG4Aspect } from "./signal_4asp.svg";
import CircuitViewSVGPopulator from "../CircuitViewSVGPopulator";
import { useGetTelemetryData } from "../../useGetTelemetryData";

type State = {
  aspect: string[];
  calling_device_id_list: string[];
  route_device_id_list: string[];
  device_id: string;
  gear_type: string;
};

const DEFAULT_STATE = {
  aspect: [],
  calling_device_id_list: [],
  route_device_id_list: [],
  device_id: "",
  gear_type: "",
};

interface Props {
  open: boolean;
  gear_type: string;
  device_id: string;
  handleClose: () => void;
  sg_type?: "MAIN" | "SHUNT";
}
const SignalCircuitViewWrapper: React.FC<Props> = (props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [state, setState] = useState<State>(DEFAULT_STATE);
  const { sg_type, gear_type, device_id } = props;
  const customerIdList = useSelector(selectedCustomersIdsSelector) as string[];

  useEffect(() => {
    async function getRequiredData() {
      setLoading(true);
      try {
        const sg_type = await getDeviceTypeForLabel(gear_type);
        const sg_type_id = sg_type.typeid;
        const sg_info = await getSignalInformation(device_id, sg_type_id);
        if (!sg_info) return;

        const { connected_calling_name_list, connected_route_name_list, aspect } = sg_info;
        const calling_device_name_set = new Set();
        const route_device_name_set = new Set();
        const calling_device_id_list = [];
        const route_device_id_list = [];

        for (const calling_device of connected_calling_name_list) {
          calling_device_name_set.add(calling_device.trim());
        }

        for (const route_device of connected_route_name_list) {
          route_device_name_set.add(route_device.trim());
        }

        const device_list_raw = await getDeviceList({
          params: { deviceTypeIds: sg_type_id, customerIds: customerIdList.toString() },
        });
        const device_list = device_list_raw.data.data;

        for (const device of device_list) {
          const name = trimAssetName(device.name);
          if (calling_device_name_set.has(name)) {
            calling_device_id_list.push(device.deviceid);
          }
          if (route_device_name_set.has(name)) {
            route_device_id_list.push(device.deviceid);
          }
        }

        setState({ aspect, calling_device_id_list, route_device_id_list, device_id, gear_type });
      } catch (error: any) {
        console.error("SIGNAL_CIRCUIT_VIEW_GET_INFORMATION", error);
        toast.error(error?.message ?? "Information not available for this Signal");
      } finally {
        setLoading(false);
      }
    }

    if (sg_type === "SHUNT") return;
    getRequiredData();
  }, [gear_type, device_id, customerIdList, sg_type]);

  if (sg_type === "SHUNT") {
    return <ShuntSignalCircuitView {...props} />;
  }

  if (loading) return <Loader />;
  return <SignalCircuitView {...state} />;
};

function getSignalSVG(aspect: string[]) {
  switch (aspect.length) {
    case 1:
      return SignalSVG1Aspect;
    case 2:
      return SignalSVG2Aspect;
    case 3:
      return SignalSVG4Aspect;
    default:
      return SignalSVG4Aspect;
  }
}

const SignalCircuitView: React.FC<State> = (props) => {
  const { aspect, calling_device_id_list, device_id, gear_type, route_device_id_list } = props;
  const required_device_id_list = useMemo(
    () => [device_id, ...calling_device_id_list, ...route_device_id_list],
    [device_id, calling_device_id_list, route_device_id_list],
  );
  const { data, loading } = useGetTelemetryData({ required_device_id_list, deviceCategory: gear_type });
  const data_map = getMainSignalDataMap(props);
  console.log("data", data);

  const svg = useMemo(() => getSignalSVG(aspect), [aspect]);

  if (loading) return <Loader />;
  return <CircuitViewSVGPopulator data={data} data_map={data_map} Component={svg} gear_type={gear_type} />;
};

export default SignalCircuitViewWrapper;
